import { LogOut, Moon, Sun, ChevronDown, Layers, Zap, FileText } from "lucide-react";
import { useState } from "react";
import { useAuth } from "../../lib/auth";
import { useFideicomiso } from "../../lib/fideicomiso";
import { useTenant } from "../../lib/tenant";
import { useTheme } from "../../lib/theme";
import { ROLE_LABELS } from "../../lib/roles";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { Button } from "../ui/button";

export default function Header() {
    const { user, logout } = useAuth();
    const { list, selected, select } = useFideicomiso();
    const { tenant, list: tenantList, isSuperAdmin, isImpersonating, switchTenant, activeTenantId } = useTenant();
    const { theme, toggle } = useTheme();
    const [open, setOpen] = useState(false);

    return (
        <header
            className="h-16 shrink-0 border-b border-border bg-card flex items-center gap-3 px-6"
            data-testid="app-header"
        >
            {/* Rebrand F15 · logo compact a la izquierda */}
            <a
                href="/"
                className="flex items-center gap-2 shrink-0"
                data-testid="header-brand"
                title="Trafalgar Servicios Fiduciarios · TRF Casa de Bolsa"
            >
                <img
                    src="/brand/logo_compact_dark.png"
                    alt="Trafalgar TRF"
                    className="h-9 w-9 rounded-sm object-contain bg-[color:var(--brand-paper)] border border-border/50"
                />
                <div className="hidden xl:flex flex-col leading-tight">
                    <span className="text-[11px] font-semibold text-[color:var(--brand-charcoal)] dark:text-[color:var(--brand-paper)]">
                        Trafalgar · TRF
                    </span>
                    <span className="text-[9px] uppercase tracking-wider text-muted-foreground">
                        Servicios Fiduciarios
                    </span>
                </div>
            </a>
            <div className="h-8 w-px bg-border/60 mx-1 hidden md:block" />
            {/* Tenant selector (solo super_admin) */}
            {isSuperAdmin && tenantList.length > 0 && (
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <button
                            className={`flex items-center gap-2 rounded-sm border px-3 py-1.5 text-sm hover:bg-muted transition-colors ${
                                isImpersonating
                                    ? "border-[color:var(--brand-coral)] bg-[color:var(--brand-coral)]/[0.06]"
                                    : "border-border"
                            }`}
                            data-testid="tenant-selector"
                        >
                            <Layers className="h-3.5 w-3.5 text-[color:var(--brand-dark-gold)]" />
                            <div className="flex flex-col items-start leading-tight">
                                <span className="text-[9px] uppercase tracking-widest text-muted-foreground">
                                    {isImpersonating ? "Operando en" : "Tenant"}
                                </span>
                                <span className="font-medium truncate max-w-[200px]">
                                    {tenant?.nombre_comercial || tenant?.nombre_legal || tenant?.slug || "—"}
                                </span>
                            </div>
                            {isImpersonating && (
                                <Zap className="h-3.5 w-3.5 text-[color:var(--brand-coral)]" />
                            )}
                            <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
                        </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start" className="w-[320px]">
                        <DropdownMenuLabel className="flex items-center justify-between">
                            <span>Cambiar tenant</span>
                            {isImpersonating && (
                                <button
                                    onClick={() => switchTenant(null)}
                                    className="text-[10px] text-[color:var(--brand-coral)] hover:underline"
                                    data-testid="tenant-restore-btn"
                                >
                                    ← volver al mío
                                </button>
                            )}
                        </DropdownMenuLabel>
                        <DropdownMenuSeparator />
                        {tenantList.map((t) => (
                            <DropdownMenuItem
                                key={t.id}
                                onSelect={() => switchTenant(t.id)}
                                data-testid={`tenant-option-${t.slug}`}
                                className="flex-col items-start gap-0.5 py-2"
                            >
                                <div className="flex items-center gap-2 w-full">
                                    <span className={`font-medium ${
                                        t.id === activeTenantId ? "text-[color:var(--brand-dark-gold)]" : ""
                                    }`}>
                                        {t.slug}
                                    </span>
                                    {t.id === user?.tenant_id && (
                                        <span className="badge-gold text-[9px]">tuyo</span>
                                    )}
                                    <span className={`ml-auto text-[10px] ${
                                        t.estado === "activo"
                                            ? "text-[color:var(--brand-exito)]"
                                            : "text-[color:var(--brand-coral)]"
                                    }`}>
                                        {t.estado}
                                    </span>
                                </div>
                                <div className="text-[11px] text-muted-foreground line-clamp-1">
                                    {t.nombre_legal}
                                </div>
                            </DropdownMenuItem>
                        ))}
                    </DropdownMenuContent>
                </DropdownMenu>
            )}
            {/* Badge tenant fijo para users no super_admin */}
            {!isSuperAdmin && tenant && (
                <div className="flex items-center gap-2 rounded-sm border border-border/60 bg-muted/30 px-3 py-1.5 text-sm"
                     data-testid="tenant-badge">
                    <Layers className="h-3.5 w-3.5 text-[color:var(--brand-dark-gold)]" />
                    <div className="flex flex-col items-start leading-tight">
                        <span className="text-[9px] uppercase tracking-widest text-muted-foreground">
                            Tenant
                        </span>
                        <span className="font-medium truncate max-w-[180px]">
                            {tenant.nombre_comercial || tenant.slug}
                        </span>
                    </div>
                </div>
            )}

            {/* Fideicomiso selector */}
            <DropdownMenu open={open} onOpenChange={setOpen}>
                <DropdownMenuTrigger asChild>
                    <button
                        className="flex items-center gap-2 rounded-sm border border-border px-3 py-1.5 text-sm hover:bg-muted transition-colors min-w-[260px]"
                        data-testid="fideicomiso-selector"
                    >
                        <div className="flex flex-col items-start leading-tight">
                            <span className="text-[10px] uppercase tracking-widest text-muted-foreground">
                                Fideicomiso activo
                            </span>
                            <span className="font-medium truncate">
                                {selected
                                    ? `${selected.numero_interno} · ${selected.tipo}`
                                    : "— seleccionar —"}
                            </span>
                        </div>
                        <ChevronDown className="ml-auto h-4 w-4 text-muted-foreground" />
                    </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-[400px]">
                    <DropdownMenuLabel>Seleccionar fideicomiso</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    {list.map((f) => (
                        <DropdownMenuItem
                            key={f.id}
                            onSelect={() => select(f.id)}
                            data-testid={`fideicomiso-option-${f.numero_interno}`}
                            className="flex-col items-start gap-0.5 py-2"
                        >
                            <div className="flex items-center gap-2 w-full">
                                <span className="font-medium">{f.numero_interno}</span>
                                <span className="badge-gold">{f.tipo}</span>
                                <span className="ml-auto text-xs text-muted-foreground">
                                    {f.numero_fiduciario}
                                </span>
                            </div>
                            <div className="text-xs text-muted-foreground line-clamp-1">
                                {f.proposito}
                            </div>
                        </DropdownMenuItem>
                    ))}
                    {list.length === 0 && (
                        <div className="px-2 py-3 text-sm text-muted-foreground">
                            Sin fideicomisos cargados.
                        </div>
                    )}
                </DropdownMenuContent>
            </DropdownMenu>

            <div className="flex-1" />

            <Button
                variant="ghost"
                size="icon"
                onClick={toggle}
                title="Alternar tema"
                data-testid="theme-toggle"
                aria-label="Alternar tema claro/oscuro"
            >
                {theme === "light" ? (
                    <Moon className="h-4 w-4" />
                ) : (
                    <Sun className="h-4 w-4" />
                )}
            </Button>

            <DropdownMenu>
                <DropdownMenuTrigger asChild>
                    <button
                        className="flex items-center gap-3 rounded-sm hover:bg-muted px-2 py-1 transition-colors"
                        data-testid="user-menu-trigger"
                    >
                        <div className="h-8 w-8 rounded-full bg-[color:var(--brand-charcoal)] text-[color:var(--brand-gold)] flex items-center justify-center text-xs font-semibold">
                            {(user?.nombre || user?.email || "?").slice(0, 2).toUpperCase()}
                        </div>
                        <div className="hidden md:flex flex-col leading-tight items-start">
                            <span className="text-sm font-medium">{user?.nombre}</span>
                            <span className="text-[11px] text-muted-foreground">
                                {(user?.roles || [])
                                    .map((r) => ROLE_LABELS[r] || r)
                                    .join(", ")}
                                {isSuperAdmin && (
                                    <span className="ml-1 text-[color:var(--brand-coral)] font-semibold">
                                        · super
                                    </span>
                                )}
                            </span>
                        </div>
                    </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                    <DropdownMenuLabel>{user?.email}</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                        asChild
                        data-testid="user-menu-ficha-tecnica"
                    >
                        <a
                            href="/api/public/ficha-tecnica.pdf"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center w-full"
                        >
                            <FileText className="h-4 w-4 mr-2" />
                            Ficha técnica (PDF)
                        </a>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                        onSelect={logout}
                        data-testid="user-menu-logout"
                        className="text-[color:var(--brand-coral)]"
                    >
                        <LogOut className="h-4 w-4 mr-2" />
                        Cerrar sesión
                    </DropdownMenuItem>
                </DropdownMenuContent>
            </DropdownMenu>
        </header>
    );
}
